import React from "react";
import { Search, RotateCw, FileSpreadsheet, FileText, ArrowLeft } from "lucide-react";

const inputClass =
  "bg-white border border-gray-200 rounded-xl md:rounded-lg text-base md:text-sm font-semibold text-gray-700 outline-none transition-all focus:border-red-500 focus:ring-2 focus:ring-red-500/10 min-h-[44px] md:min-h-0";

/**
 * Thanh lọc tab "Báo cáo điểm danh".
 */
const AttendanceReportFilter = ({
  yearMonth,
  onYearMonthChange,
  courseId = "",
  onCourseChange,
  courses = [],
  searchTerm = "",
  onSearchChange,
  isMobile,
  onRefresh,
  onExport,
  onExportPdf,
  exporting = false,
  exportingPdf = false,
  canExport = false,
  detailClass = null,
  onBack,
}) => {
  if (detailClass) {
    return (
      <div
        className={`bg-white rounded-xl border border-gray-100 shadow-sm sticky top-14 md:static z-10 ${
          isMobile ? "p-3 space-y-2.5" : "p-4 space-y-3"
        }`}
        data-tour="attendance-report-filter"
      >
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onBack?.()}
            className="h-10 md:h-9 px-3 inline-flex items-center gap-1.5 rounded-xl md:rounded-lg border border-gray-200 bg-white text-xs font-bold text-gray-600 active:text-red-600 active:border-red-200 transition-all touch-manipulation shrink-0"
          >
            <ArrowLeft size={14} />
            {isMobile ? "Lớp" : "Danh sách lớp"}
          </button>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-black text-gray-900 truncate">
              {detailClass.class_name}
            </p>
            <p className="text-[11px] text-gray-400 font-semibold truncate">
              {[detailClass.class_code, detailClass.course_name]
                .filter(Boolean)
                .join(" · ") || "Chi tiết học viên"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative group flex-1">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500"
              size={16}
            />
            <input
              type="search"
              enterKeyHint="search"
              placeholder="Tìm học viên..."
              value={searchTerm}
              onChange={(e) => onSearchChange?.(e.target.value)}
              className={`w-full pl-10 pr-3 py-2 ${inputClass}`}
            />
          </div>
          <button
            type="button"
            onClick={() => onRefresh?.()}
            className="h-11 w-11 md:h-9 md:w-9 flex items-center justify-center rounded-xl md:rounded-lg border border-gray-200 bg-white text-gray-500 active:text-red-500 transition-all shrink-0 touch-manipulation"
            title="Làm mới"
          >
            <RotateCw size={16} />
          </button>
          <button
            type="button"
            onClick={() => onExport?.()}
            disabled={!canExport || exporting || exportingPdf}
            className={`h-11 md:h-9 px-3 inline-flex items-center gap-1.5 rounded-xl md:rounded-lg text-xs font-bold transition-all touch-manipulation ${
              canExport && !exporting && !exportingPdf
                ? "bg-emerald-600 text-white active:bg-emerald-700 shadow-sm"
                : "bg-gray-100 text-gray-400 cursor-not-allowed"
            }`}
            title="Xuất Excel"
          >
            <FileSpreadsheet size={15} />
            {exporting ? "..." : isMobile ? "Excel" : "Xuất Excel"}
          </button>
          <button
            type="button"
            onClick={() => onExportPdf?.()}
            disabled={!canExport || exporting || exportingPdf}
            className={`h-11 md:h-9 px-3 inline-flex items-center gap-1.5 rounded-xl md:rounded-lg text-xs font-bold transition-all touch-manipulation ${
              canExport && !exporting && !exportingPdf
                ? "bg-red-600 text-white active:bg-red-700 shadow-sm"
                : "bg-gray-100 text-gray-400 cursor-not-allowed"
            }`}
            title="Xuất PDF"
          >
            <FileText size={15} />
            {exportingPdf ? "..." : isMobile ? "PDF" : "Xuất PDF"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`bg-white rounded-xl border border-gray-100 shadow-sm ${
        isMobile ? "p-3 space-y-2.5" : "p-4 space-y-3"
      }`}
      data-tour="attendance-report-filter"
    >
      <div className={isMobile ? "space-y-2" : "flex flex-wrap items-center gap-2.5"}>
        <div className={`flex gap-2 ${isMobile ? "w-full" : ""}`}>
          <input
            type="month"
            value={yearMonth || ""}
            onChange={(e) => onYearMonthChange?.(e.target.value)}
            className={`${isMobile ? "flex-1" : "w-auto shrink-0"} px-3 py-2 ${inputClass}`}
            title="Tháng báo cáo (tuỳ chọn)"
          />
          {!isMobile ? null : (
            <button
              type="button"
              onClick={() => onRefresh?.()}
              className="h-11 w-11 flex items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 active:text-red-500 transition-all shrink-0 touch-manipulation"
              title="Làm mới"
            >
              <RotateCw size={16} />
            </button>
          )}
        </div>

        <select
          value={courseId}
          onChange={(e) => onCourseChange?.(e.target.value)}
          className={`${isMobile ? "w-full" : "min-w-[180px] max-w-xs"} px-3 py-2 ${inputClass}`}
        >
          <option value="">Tất cả khóa học</option>
          {courses.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>

        <div className={`relative group ${isMobile ? "w-full" : "w-full max-w-sm"}`}>
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500"
            size={16}
          />
          <input
            type="search"
            enterKeyHint="search"
            placeholder={isMobile ? "Tìm lớp..." : "Tìm lớp, mã lớp, khóa..."}
            value={searchTerm}
            onChange={(e) => onSearchChange?.(e.target.value)}
            className={`w-full pl-10 pr-3 py-2 ${inputClass}`}
          />
        </div>

        <div className={`flex items-center gap-2 ${isMobile ? "w-full" : "ml-auto"}`}>
          {!isMobile ? (
            <button
              type="button"
              onClick={() => onRefresh?.()}
              className="h-9 w-9 flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 hover:text-red-500 hover:border-red-200 transition-all shrink-0"
              title="Làm mới"
            >
              <RotateCw size={16} />
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => onExport?.()}
            disabled={!canExport || exporting || exportingPdf}
            className={`h-11 md:h-9 px-3 inline-flex items-center gap-1.5 rounded-xl md:rounded-lg text-xs font-bold transition-all touch-manipulation ${
              canExport && !exporting && !exportingPdf
                ? "bg-emerald-600 text-white active:bg-emerald-700 shadow-sm"
                : "bg-gray-100 text-gray-400 cursor-not-allowed"
            } ${isMobile ? "flex-1 justify-center" : ""}`}
            title="Xuất Excel"
          >
            <FileSpreadsheet size={15} />
            {exporting ? "Đang xuất..." : "Xuất Excel"}
          </button>
          <button
            type="button"
            onClick={() => onExportPdf?.()}
            disabled={!canExport || exporting || exportingPdf}
            className={`h-11 md:h-9 px-3 inline-flex items-center gap-1.5 rounded-xl md:rounded-lg text-xs font-bold transition-all touch-manipulation ${
              canExport && !exporting && !exportingPdf
                ? "bg-red-600 text-white active:bg-red-700 shadow-sm"
                : "bg-gray-100 text-gray-400 cursor-not-allowed"
            } ${isMobile ? "flex-1 justify-center" : ""}`}
            title="Xuất PDF"
          >
            <FileText size={15} />
            {exportingPdf ? "Đang xuất..." : "Xuất PDF"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AttendanceReportFilter;
